import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Card, CardContent } from '~/components/ui/card';
import { UpdateFamilyMutation } from './useUpdateFamily';
import type { EditFamilyInfoDialogProps } from './types';

interface Contact {
  name: string;
  phone: string;
  relationship: string;
}

function ContactList({ contacts, onChange, label }: { contacts: Contact[]; onChange: (contacts: Contact[]) => void; label: string }) {
  const addContact = () => {
    onChange([...contacts, { name: '', phone: '', relationship: '' }]);
  };

  const updateContact = (index: number, field: string, value: string) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    onChange(newContacts);
  };

  const removeContact = (index: number) => {
    onChange(contacts.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" variant="outline" size="sm" onClick={addContact}>
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No {label.toLowerCase()} added yet.</p>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact, index) => (
            <Card key={index}>
              <CardContent className="pt-4 pb-3">
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Name"
                    value={contact.name}
                    onChange={(e) => updateContact(index, 'name', e.target.value)}
                  />
                  <Input
                    placeholder="Phone"
                    value={contact.phone}
                    onChange={(e) => updateContact(index, 'phone', e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Input
                      placeholder="Relationship"
                      value={contact.relationship}
                      onChange={(e) => updateContact(index, 'relationship', e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-destructive hover:text-destructive"
                      onClick={() => removeContact(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function EditFamilyInfoDialog({ open, onOpenChange, family, onSuccess }: EditFamilyInfoDialogProps) {
  const [formData, setFormData] = useState({
    emergencyContacts: [] as Contact[],
    authorizedPickup: [] as Contact[],
    churchName: '',
    pastorName: '',
    churchEmail: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  });

  const [updateFamily, { loading }] = useMutation(UpdateFamilyMutation, {
    onCompleted: (result) => {
      if (result.updateFamily?.family) {
        onSuccess?.();
        onOpenChange(false);
      }
    },
  });

  // Initialize form data from family
  useEffect(() => {
    if (family && open) {
      setFormData({
        emergencyContacts: family?.emergencyContacts?.map((c: { name: string; phone: string | null; relationship: string | null }) => ({
          name: c.name,
          phone: c.phone ?? '',
          relationship: c.relationship ?? '',
        })) || [],
        authorizedPickup: family?.authorizedPickup?.map((c: { name: string; phone: string | null; relationship: string | null }) => ({
          name: c.name,
          phone: c.phone ?? '',
          relationship: c.relationship ?? '',
        })) || [],
        churchName: family.churchAffiliation?.churchName || '',
        pastorName: family.churchAffiliation?.pastorName || '',
        churchEmail: family.churchAffiliation?.churchEmail || '',
        street: family.address?.street || '',
        street2: family.address?.street2 || '',
        city: family.address?.city || '',
        state: family.address?.state || '',
        zip: family.address?.zip || '',
        country: family.address?.country || '',
      });
    }
  }, [family, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Filter out empty contacts
    const validEmergencyContacts = formData.emergencyContacts
      .filter(c => c.name?.trim())
      .map(c => ({
        name: c.name,
        phone: c.phone || null,
        relationship: c.relationship || null,
      }));

    const validAuthorizedPickup = formData.authorizedPickup
      .filter(c => c.name?.trim())
      .map(c => ({
        name: c.name,
        phone: c.phone || null,
        relationship: c.relationship || null,
      }));

    const churchAffiliation = (formData.churchName || formData.pastorName || formData.churchEmail)
      ? {
          churchName: formData.churchName || null,
          pastorName: formData.pastorName || null,
          churchEmail: formData.churchEmail || null,
        }
      : null;

    const address = (formData.street || formData.city || formData.state || formData.zip)
      ? {
          street: formData.street || null,
          street2: formData.street2 || null,
          city: formData.city || null,
          state: formData.state || null,
          zip: formData.zip || null,
          country: formData.country || null,
        }
      : null;

    updateFamily({
      variables: {
        familyId: family!.id!,
        emergencyContacts: validEmergencyContacts,
        authorizedPickup: validAuthorizedPickup,
        churchAffiliation,
        address,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Family Information</DialogTitle>
          <DialogDescription>
            Update emergency contacts, authorized pickup, and church affiliation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="address" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="address">Address</TabsTrigger>
              <TabsTrigger value="emergency">Emergency</TabsTrigger>
              <TabsTrigger value="pickup">Pickup</TabsTrigger>
              <TabsTrigger value="church">Church</TabsTrigger>
            </TabsList>

            <TabsContent value="address" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="street">Street Address</Label>
                <Input
                  id="street"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  placeholder="123 Main St"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="street2">Apt/Suite (optional)</Label>
                <Input
                  id="street2"
                  value={formData.street2}
                  onChange={(e) => setFormData({ ...formData, street2: e.target.value })}
                  placeholder="Apt 4B"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    placeholder="12345"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country (optional)</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="USA"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="emergency" className="mt-4">
              <ContactList
                contacts={formData.emergencyContacts}
                onChange={(contacts) => setFormData({ ...formData, emergencyContacts: contacts })}
                label="Emergency Contacts"
              />
            </TabsContent>

            <TabsContent value="pickup" className="mt-4">
              <ContactList
                contacts={formData.authorizedPickup}
                onChange={(contacts) => setFormData({ ...formData, authorizedPickup: contacts })}
                label="Authorized Pickup Persons"
              />
            </TabsContent>

            <TabsContent value="church" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="churchName">Church Name</Label>
                <Input
                  id="churchName"
                  value={formData.churchName}
                  onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
                  placeholder="Church name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pastorName">Pastor Name</Label>
                <Input
                  id="pastorName"
                  value={formData.pastorName}
                  onChange={(e) => setFormData({ ...formData, pastorName: e.target.value })}
                  placeholder="Pastor name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="churchEmail">Church Email</Label>
                <Input
                  id="churchEmail"
                  type="email"
                  value={formData.churchEmail}
                  onChange={(e) => setFormData({ ...formData, churchEmail: e.target.value })}
                  placeholder="church@example.com"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditFamilyInfoDialog;
